import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
import { Icon } from '@iconify/react';
import axios from 'axios'; // Ensure axios is imported
import { Form, FormikProvider, useFormik } from 'formik';
import Cookies from 'js-cookie';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

// Material UI
import { Alert, Box, IconButton, InputAdornment, Snackbar, Stack, TextField } from '@mui/material';
import { GlobalStyles } from '@mui/system';

import { LoadingButton } from '@mui/lab';

import { LoginFormValues } from '../types/LoginForm';

const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarType, setSnackbarType] = useState<'success' | 'error' | 'warning' | 'info'>(
    'success'
  );
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');

  // Validation Schema
  const LoginSchema = Yup.object().shape({
    email: Yup.string().email('Invalid email').required('Email is required'),
    password: Yup.string().required('Password is required'),
  });

  // Formik Hook
  const formik = useFormik<LoginFormValues>({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: LoginSchema,
    onSubmit: async (values) => {
      try {
        const url = `${process.env.REACT_APP_BASE_URL}/auth/login`;
        const response = await axios.post(url, {
          email: values.email,
          password: values.password,
        });

        if (response.status === 200) {
          const { token, user } = response.data.data;
          const expires = new Date(new Date().getTime() + 12 * 60 * 60 * 1000);
          Cookies.set('token', token, {
            secure: true,
            sameSite: 'strict', // Cookie will only be sent in a first-party context
            expires: expires,
          });
          Cookies.set('role', user.role, {
            secure: true,
            sameSite: 'strict',
            expires: expires,
          });
          Cookies.set('username', user.username, {
            secure: true,
            sameSite: 'strict',
            expires: expires,
          });
          navigate('/dashboard', { replace: true });
        } else {
          setSnackbarType('error');
          setSnackbarMessage(response.data.message);
          setSnackbarOpen(true);
        }
      } catch (error) {
        console.error('Login error:', error);
        if (axios.isAxiosError(error)) {
          setSnackbarType('error');
          setSnackbarMessage(error.response?.data?.message || 'An error occurred during login');
          setSnackbarOpen(true);
        } else {
          setSnackbarType('error');
          setSnackbarMessage('An unexpected error occurred');
          setSnackbarOpen(true);
        }
      }
    },
  });

  const { errors, touched, handleSubmit, getFieldProps, isSubmitting } = formik;

  return (
    <>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarType}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <FormikProvider value={formik}>
        <Form autoComplete='off' noValidate onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {/* Email Field */}
            <TextField
              fullWidth
              type='email'
              label='Email'
              placeholder='Enter your email'
              {...getFieldProps('email')}
              error={Boolean(touched.email && errors.email)}
              helperText={touched.email && errors.email}
              InputProps={{
                sx: {
                  color: 'white',
                  '& input': {
                    color: 'white', // Normal input typing color
                  },
                  '& input:-webkit-autofill': {
                    WebkitBoxShadow: '0 0 0 1000px transparent inset', // match dark background
                    WebkitTextFillColor: 'white',
                    transition: 'background-color 5000s ease-in-out 0s',
                    caretColor: 'white',
                  },
                },
              }}
            />

            {/* Password Field */}
            <TextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              label='Password'
              placeholder='Enter your password'
              {...getFieldProps('password')}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge='end'
                      aria-label='toggle password visibility'
                    >
                      <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  color: 'white',
                  '& input': {
                    color: 'white',
                  },
                  '& input:-webkit-autofill': {
                    WebkitBoxShadow: '0 0 0 1000px transparent inset', // dark background color
                    WebkitTextFillColor: 'white',
                    transition: 'background-color 5000s ease-in-out 0s',
                    caretColor: 'white',
                  },
                },
              }}
              error={Boolean(touched.password && errors.password)}
              helperText={touched.password && errors.password}
            />
          </Stack>

          <GlobalStyles
            styles={{
              '@keyframes dotPulse': {
                '0%, 100%': {
                  transform: 'scale(1)',
                  opacity: 0.6,
                },
                '50%': {
                  transform: 'scale(1.5)',
                  opacity: 1,
                },
              },
            }}
          />

          {/* Submit Button */}
          <LoadingButton
            fullWidth
            size='large'
            type='submit'
            variant='contained'
            loading={isSubmitting}
            sx={{
              backgroundColor: '#6871d1',
              color: '#fff',
              mt: 4,
              py: 1.5,
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(104, 113, 209, 0.4)',
              transition: 'all 0.3s ease',
              minHeight: '48px', // Fixed height
              minWidth: '120px', // Fixed width
              position: 'relative',
              '&:hover': {
                backgroundColor: '#5a63bb',
                boxShadow: '0 6px 16px rgba(104, 113, 209, 0.6)',
              },
              '&:disabled': {
                backgroundColor: '#aeb3e9', // lighter shade for disabled
                color: '#fff',
                opacity: 0.7, // reduced opacity for clarity
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                position: 'relative',
              }}
            >
              {/* Loader (visible only when loading) */}
              <Box
                sx={{
                  display: isSubmitting ? 'flex' : 'none',
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: 0,
                  bottom: 0,
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 2,
                }}
              >
                <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  {[...Array(3)].map((_, i) => (
                    <Box
                      key={i}
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: '#fff',
                        animation: `dotPulse 1s ease-in-out ${i * 0.2}s infinite`,
                      }}
                    />
                  ))}
                </Box>
              </Box>
              {/* Button Text (hidden when loading) */}
              <Box
                sx={{
                  opacity: isSubmitting ? 0 : 1,
                  transition: 'opacity 0.2s',
                  width: '100%',
                  textAlign: 'center',
                  zIndex: 1,
                }}
              >
                Log In
              </Box>
            </Box>
          </LoadingButton>
        </Form>
      </FormikProvider>
    </>
  );
};

export default LoginForm;
